import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import mask from 'assets/mask.svg';
import MediaPackCard from 'components/characters/MediaPackCard';
import GameLike from 'components/games/tools/GameLike';
import CommonHelper from 'helpers/CommonHelper';
import { useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import styled, { css } from 'styled-components';


const AssetDialogCard = (props) => {
  const {
    coverUrl,
    publicName,
    publicDescription,
    gameId,
    charactersWithInfoCount = 0,
    tags = [],
    isPublic,
    videoCoverUrl,
    scenario,
    game,
    setIsSubscriptionModalOpen,
    setIsSignUpModalOpen,
    setIsEmailConfirmationModalOpen
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isMobilePlayed, setIsMobilePlayed] = useState(false);

  const navigate = useNavigate();

  const videoRef = useRef(null);

  const scenarioMediaPacks = scenario?.mediaPacks ?? [];

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: currentUser,
        hasSubscription
      };
    }, [])
  );

  const handleTagClick = tag => {
    if (!tag?.hasPage) {
      return;
    }
    const formattedTagName = tag?.name.split(' ').join('_');
    navigate(GUEST_ROUTES.TAG(formattedTagName));
  };

  const sortedTagsByHasPageFlag = useMemo(() => {
    return [...tags].sort(a => (a.hasPage ? -1 : 1));
  }, [tags]);

  const longPrivateDescription = !isPublic && publicDescription?.length > 90;
  const isVisibleExpandedBlock = charactersWithInfoCount > 0 || longPrivateDescription;

  const shouldPlayVideo = ((isHovered && shouldLoadVideo) || (isMobile && !isMobilePlayed)) && videoCoverUrl;

  const handleMouseEnter = () => {
    setIsHovered(true);
    setTimeout(() => setShouldLoadVideo(true), 300);
  };

  const handleVideoError = () => {
    setShouldLoadVideo(false);
  };

  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered]);

  useEffect(() => {
    if (isMobile && videoRef.current && !isMobilePlayed) {
      videoRef.current.loop = false;

      videoRef.current.play().then(() => {
        videoRef.current.onended = () => {
          setIsMobilePlayed(true);
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        };
      });
    }
  }, [isMobile, isMobilePlayed]);


  return (
    <Wrapper>
      <TopInfo onMouseEnter={handleMouseEnter} onMouseLeave={() => setIsHovered(false)}>
        {shouldPlayVideo && (
          <TopVideo
            ref={videoRef}
            src={videoCoverUrl}
            autoPlay
            loop
            muted
            playsInline
            isVisible={shouldPlayVideo}
            onError={handleVideoError}
          />
        )}

        {!shouldPlayVideo && coverUrl && (
          <TopImage src={CommonHelper.getFilesCdnUrl(coverUrl)} alt="cover" />
        )}

        {!shouldPlayVideo && !coverUrl && (
          <NoImagePlaceholder>
            <DefaultImage src={mask} alt="cover" />
          </NoImagePlaceholder>
        )}

        {(gameId || currentUser) && (
          <LikePlaceholder>
            <GameLike gameId={gameId} size={36} />
          </LikePlaceholder>
        )}
      </TopInfo>


      <ModalContentPlaceholder
        style={{ paddingLeft: '30px', paddingRight: '30px', paddingBottom: '20px' }}
      >
        <ModalHeading>{publicName}</ModalHeading>
        <TagsWrapper>
          {sortedTagsByHasPageFlag.map(tag => {
            if (!tag?.name) {
              return null;
            }

            if (tag?.hasPage) {
              const formattedTagName = tag.name.split(' ').join('_');
              return (
                <TagLink
                  key={tag.name}
                  href={GUEST_ROUTES.TAG(formattedTagName)}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTagClick(tag);
                  }}
                >
                  {tag.name}
                </TagLink>
              );
            }

            return (
              <TagDiv key={tag.name}>
                {tag.name}
              </TagDiv>
            );
          })}
        </TagsWrapper>
        <ModalContent isMediaPacksVisible={scenarioMediaPacks.length > 0}>
          <TextContent longPrivateDescription={longPrivateDescription} isCharactersWithInfo={charactersWithInfoCount > 0} isExpanded={isExpanded}>
            {publicDescription}
          </TextContent>
          {isVisibleExpandedBlock && !isExpanded && (
            <ExpandButton isVisible onClick={() => setIsExpanded(true)}>Expand</ExpandButton>
          )}
        </ModalContent>
        {scenarioMediaPacks.length > 0 && hasSubscription && (
          <MediaPackWrapper>
            {scenarioMediaPacks.map(mediaPack => (
              <MediaPackCard
                key={mediaPack._id}
                mediaPack={mediaPack}
                scenario={scenario}
                game={game}
                setIsSubscriptionModalOpen={setIsSubscriptionModalOpen}
                setIsSignUpModalOpen={setIsSignUpModalOpen}
                setIsEmailConfirmationModalOpen={setIsEmailConfirmationModalOpen}
              />
            ))}
          </MediaPackWrapper>
        )}
      </ModalContentPlaceholder>
    </Wrapper>
  );
};

export default AssetDialogCard;

const TopInfo = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
`;

const TopVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  position: absolute;
`;


const TopImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const ModalContentPlaceholder = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  color: white;
  padding-bottom: 24px;
`;

const ModalHeading = styled.div`
  margin-bottom: 5px;
  font-size: 20px;
  font-family: 'Nunito Sans';
  font-weight: bold;
`;

const ModalContent = styled.div`
  position: relative;
`;

const TextContent = styled.div`
  font-size: 16px;
  line-height: 18px;
  font-family: 'Nunito Sans';
  word-break: break-word;

  ${props =>
    (props.isCharactersWithInfo || props.longPrivateDescription) &&
    !props.isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 24px;
  `}
`;


const ExpandButton = styled.button`
  position: absolute;
  bottom: -30px;
  left: 0;
  background: transparent;
  color: #808080;
  border: none;
  padding: 8px 8px 8px 0px;
  cursor: pointer;
  font-size: 16px;
  text-decoration: underline;
  font-family: 'Nunito Sans';
  line-height: 18px;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
`;


const LikePlaceholder = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const Wrapper = styled.div`
  background: rgba(29, 29, 29, 1);
  border-radius: 12px;

  @media (max-width: 600px) {
    max-height: 90vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const MediaPackWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 24px;

  @media (max-width: 600px) {
  // Отступ для реальных девайсов для того чтобы было видно с шторкой урлы для сафари
    padding-bottom: 164px;
  }
`;


const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0;
  padding: 24px;
`;

const DefaultImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0;
`;

const TagBase = css`
  font-family: Nunito Sans;
  font-size: 12px;
  font-weight: 400;
  padding: 4px 6px;
  border-radius: 6px;
  background: #131313;
  color: #a9a9a9;
  transition: transform 0.1s ease;
`;

const TagLink = styled.a`
  ${TagBase}
  cursor: pointer;
  text-decoration: underline;
  
  :hover {
    transform: scale(1.1);
  }
`;

const TagDiv = styled.div`
  ${TagBase}
  cursor: default;
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;
